// Minimal EVM wallet helpers — pure browser, no external deps.
// Uses the standard `window.ethereum` provider (MetaMask, TrustWallet, TokenPocket, etc.).

const USDT_TRANSFER_SELECTOR = "0xa9059cbb";

export function pad32(hex) {
  return hex.replace(/^0x/, "").toLowerCase().padStart(64, "0");
}

// Amount as decimal string / number → 32-byte hex big-endian, using decimals (18 for BSC USDT)
// Uses BigInt to avoid float issues.
export function encodeAmount(amount, decimals) {
  const [whole, frac = ""] = String(amount).split(".");
  const clean = (frac + "0".repeat(decimals)).slice(0, decimals);
  const raw = BigInt(whole) * (10n ** BigInt(decimals)) + BigInt(clean || "0");
  return pad32(raw.toString(16));
}

export function encodeErc20Transfer(recipient, amount, decimals) {
  const addr = pad32(recipient.replace(/^0x/, ""));
  const amt = encodeAmount(amount, decimals);
  return USDT_TRANSFER_SELECTOR + addr + amt;
}

export function chainIdToHex(id) {
  return "0x" + Number(id).toString(16);
}

export const CHAIN_META = {
  56: {
    chainId: "0x38",
    chainName: "BNB Smart Chain",
    nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
    rpcUrls: ["https://bsc-dataseed.binance.org/"],
    blockExplorerUrls: ["https://bscscan.com"],
  },
  97: {
    chainId: "0x61",
    chainName: "BSC Testnet",
    nativeCurrency: { name: "tBNB", symbol: "tBNB", decimals: 18 },
    rpcUrls: ["https://data-seed-prebsc-1-s1.binance.org:8545/"],
    blockExplorerUrls: ["https://testnet.bscscan.com"],
  },
};

export async function connectWallet() {
  if (!window.ethereum) {
    throw new Error("No EVM wallet detected. Please install MetaMask, TrustWallet, TokenPocket, or a compatible browser wallet.");
  }
  const accs = await window.ethereum.request({ method: "eth_requestAccounts" });
  if (!accs || accs.length === 0) throw new Error("No account returned by wallet");
  return accs[0];
}

export async function currentChainId() {
  if (!window.ethereum) return null;
  const cid = await window.ethereum.request({ method: "eth_chainId" });
  return parseInt(cid, 16);
}

export async function switchToChain(chainId) {
  if (!window.ethereum) throw new Error("No wallet");
  const hex = chainIdToHex(chainId);
  try {
    await window.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: hex }] });
  } catch (err) {
    if (err.code === 4902 && CHAIN_META[chainId]) {
      await window.ethereum.request({ method: "wallet_addEthereumChain", params: [CHAIN_META[chainId]] });
    } else {
      throw err;
    }
  }
}

export async function sendUsdtTransfer({ from, contract, recipient, amount, decimals }) {
  const data = encodeErc20Transfer(recipient, amount, decimals);
  const txHash = await window.ethereum.request({
    method: "eth_sendTransaction",
    params: [{ from, to: contract, data, value: "0x0" }],
  });
  return txHash;
}
